import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { ChefHat, Users, Utensils, LogOut, Clock, BarChart3, Video, Sparkles, Share2, ShoppingBag, Database, ShieldAlert } from 'lucide-react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { AdminOTPForm } from './AdminOTPForm';
import { useSessionManager } from '../../hooks/useSessionManager';

export function AdminLayout() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(() => localStorage.getItem('isOtpVerified') === 'true');
  const [error, setError] = useState<string | null>(null);
  const [sessionWarning, setSessionWarning] = useState<string | null>(null);
  
  // Check if we're in test environment to skip MFA
  const isTestEnvironment = import.meta.env.VITE_ENV === 'test';

  const { checkOtpExpiration, setOtpVerified } = useSessionManager({
    inactivityTimeout: 60 * 60 * 1000, // 1 hour
    otpExpirationTime: 2 * 60 * 60 * 1000, // 2 hours
    onSessionExpired: () => {
      setSessionWarning('Session expired due to inactivity. Please log in again.');
      setIsAuthenticated(false);
      setIsOtpVerified(false);
    },
    onOtpExpired: () => {
      setSessionWarning('Security verification expired. Please verify again.');
      setIsOtpVerified(false);
    }
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
      // Reset OTP verification if user logs out
      if (!user) {
        setIsOtpVerified(false);
        localStorage.removeItem('isOtpVerified');
        localStorage.removeItem('otpVerifiedAt');
      } else {
        // Check OTP expiration when user is authenticated
        if (isTestEnvironment) {
          // Skip OTP verification in test environment
          setIsOtpVerified(true);
        } else {
          const isValidOtp = checkOtpExpiration();
          setIsOtpVerified(isValidOtp);
        }
      }
    });

    return () => unsubscribe();
  }, [checkOtpExpiration]);

  // Clear session warning after 5 seconds
  useEffect(() => {
    if (sessionWarning) {
      const timer = setTimeout(() => {
        setSessionWarning(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [sessionWarning]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSessionWarning(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      console.error('Login error:', error);
      setError('Invalid credentials');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsOtpVerified(false);
      localStorage.removeItem('isOtpVerified');
      localStorage.removeItem('otpVerifiedAt');
      setSessionWarning(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleOtpVerified = () => {
    setOtpVerified();
    setIsOtpVerified(true);
    setSessionWarning(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-menioo-cream flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="rounded-full bg-white p-3 shadow-md">
                <ChefHat className="h-12 w-12 text-menioo-green" />
              </div>
            </div>
            <h2 className="mt-6 text-3xl font-display font-bold text-menioo-navy">
              Admin Access
            </h2>
          </div>

          {sessionWarning && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md flex items-center">
              <Clock className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="text-sm">{sessionWarning}</span>
            </div>
          )}

          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            {error && (
              <div className="text-red-600 text-center">
                {error}
              </div>
            )}

            <div className="rounded-md shadow-sm space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-menioo-navy">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-menioo-navy">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-menioo-green"
            >
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  // MFA/OTP step: only allow access if OTP is verified
  if (isAuthenticated && !isOtpVerified && !isTestEnvironment) {
    return (
      <AdminOTPForm
        email="calvet.alex@gmail.com"
        onVerify={handleOtpVerified}
        onCancel={handleLogout}
        sessionWarning={sessionWarning}
      />
    );
  }

  return (
    <div className="min-h-screen bg-menioo-cream">
      {/* Session Warning Banner */}
      {sessionWarning && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center">
            <Clock className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0" />
            <span className="text-sm text-amber-800">{sessionWarning}</span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-3">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <ChefHat className="h-8 w-8 text-menioo-green" />
              <span className="ml-2 text-xl font-display font-bold text-menioo-navy whitespace-nowrap">
                Menioo Admin
              </span>
            </div>

            {/* Navigation Links — scroll horizontal si trop d’onglets */}
            <div className="flex-1 min-w-0 overflow-x-auto">
              <div className="flex items-stretch gap-4 h-16 whitespace-nowrap">
                <Link
                  to="/admin/ingredients"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname.startsWith('/admin/ingredients')
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <Utensils className="h-4 w-4 mr-2" />
                  Ingredients
                </Link>
                <Link
                  to="/admin/organizations"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname.startsWith('/admin/organizations') &&
                    !location.pathname.startsWith('/admin/organizations/admin')
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Organizations
                </Link>
                <Link
                  to="/admin/organizations/admin"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname.startsWith('/admin/organizations/admin')
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <ShieldAlert className="h-4 w-4 mr-2" />
                  Organisation Admin
                </Link>
                <Link
                  to="/admin/reports"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname === '/admin/reports'
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Reports
                </Link>
                <Link
                  to="/admin/recipes"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname === '/admin/recipes'
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <ChefHat className="h-4 w-4 mr-2" />
                  Recipes
                </Link>
                <Link
                  to="/admin/tiktok"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname === '/admin/tiktok'
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <Video className="h-4 w-4 mr-2" />
                  TikTok Recipe
                </Link>
                <Link
                  to="/admin/recipe-creator"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname === '/admin/recipe-creator'
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Recipe Creator
                </Link>
                <Link
                  to="/admin/social-import"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname === '/admin/social-import'
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Social Import
                </Link>
                <Link
                  to="/admin/scraping"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname === '/admin/scraping'
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Scraping
                </Link>
                <Link
                  to="/admin/mass-db"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium flex-shrink-0 ${
                    location.pathname.startsWith('/admin/mass-db')
                      ? 'text-menioo-green border-b-2 border-menioo-green'
                      : 'text-gray-500 hover:text-menioo-navy'
                  }`}
                >
                  <Database className="h-4 w-4 mr-2" />
                  Mass DB
                </Link>
              </div>
            </div>

            {/* Logout — toujours visible à droite */}
            <div className="flex-shrink-0 flex items-center">
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-600 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </div>
    </div>
  );
}